/**
 * The canonical word segmentation, and `fold()` — the Arabic normalisation used
 * IDENTICALLY by the anchor hash, the Qur'an search index and the notes index.
 * SPEC §3.1, §3.2, §1.5 gate 5.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE IS A FROZEN CONTRACT. See the banner in `types.ts`.
 *
 * `fold()` IS DEFINED EXACTLY ONCE — here. SPEC §3.1 says it is "shared
 * verbatim between `pipeline/fold.ts` and `src/lib/text/fold.ts`, asserted
 * byte-identical by `fold.spec.ts`". Sharing a function by copying it and then
 * testing that the copies still match is a weaker arrangement than not copying
 * it, so both of those files exist and both are one-line RE-EXPORTS of this
 * definition; `src/lib/text/fold.spec.ts` asserts function identity (`===`, not
 * "byte-identical text") across all three entry points and pins a golden vector
 * table that any future re-implementation must reproduce.
 *
 * If the anchor hash, the Qur'an index and the notes index ever disagreed about
 * `fold()`, every `text_hash_fold` minted on one side would read as drift on the
 * other and the Qur'an index would stop matching the queries typed against it.
 * One definition is the only arrangement in which that cannot happen.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { QUOTE_MAX_CHARS } from './types';

/**
 * The identifier of the `fold()` rule set. Any change to the rules below is a
 * change to `text_hash_fold` for every remark ever written, so it comes with a
 * bump here, a bump of `ANCHOR_SPACE_VERSION`, and a rebuild of both indexes.
 */
export const FOLD_VERSION = 'fold-1';

/**
 * The identifier of the segmentation rule set. Pairs with `FOLD_VERSION` to
 * make up `ANCHOR_SPACE_VERSION`.
 */
export const SEGMENT_VERSION = 'seg-1';

/* ───────────────────────── fold() ─────────────────────────────────────────
 *
 * SPEC §3.1's clause list, in the order the spec writes it, plus the two
 * corrections recorded below:
 *
 *   0. Unicode NFC          — see WHY NFC
 *   1. strip tatweel        U+0640
 *   2. strip tashkeel       U+064B..U+0655   (fathatan..hamza below)
 *                           U+0670           (superscript alef / dagger alef)
 *                           U+06D6..U+06ED   (Qur'anic annotation: the waqf and
 *                                             pause marks, the small high seen,
 *                                             the sajdah sign, the small waw and
 *                                             ya, the small high meem)
 *   3. alef variants        U+0622 آ, U+0623 أ, U+0625 إ, U+0671 ٱ  →  U+0627 ا
 *   4. alef maqsura         U+0649 ى → U+064A ي
 *   5. ta marbuta           U+0629 ة → U+0647 ه
 *   6. collapse whitespace  /\s+/ → ' ', then trim
 *
 * WHY U+0653..U+0655 AND WHY NFC — TWO DEPARTURES FROM THE SPEC'S CLAUSE LIST
 * ──────────────────────────────────────────────────────────────────────────
 * SPEC §3.1 lists the tashkeel range as U+064B..U+0652 and specifies no
 * normalisation step. Measured against the text SPEC §1.5 actually vendors,
 * that clause list does not do what the sentence around it promises:
 *
 *   5,376 × U+0653 (maddah above) and 773 × U+0654 (hamza above) survive it,
 *   across 3,344 of the 6,236 ayat — 53.6% of the mushaf.
 *
 * Concretely, the Uthmani text spells ٱلضَّآلِّينَ with U+0627 followed by
 * U+0653, so the spec's clause list folds it to a string still carrying that
 * mark, while the same word typed on an ordinary keyboard folds to a string
 * without it. The two spellings of one word do not fold alike, which is a live
 * search bug (a user typing ءامنوا never matches the indexed ءامنوٓا) and would
 * be a migration the day it was corrected, because `text_hash_fold` is stored
 * on every note.
 *
 * Widening clause 2 to U+064B..U+0655 and adding an NFC pass takes the residual
 * on the same corpus to ZERO (asserted in `anchor.spec.ts`). NFC is what makes
 * the hamza carriers agree: the decomposed U+0648 U+0654 and the precomposed
 * U+0624 ؤ are the same letter, and only a normalisation step can see that.
 * NFC and not NFD because NFC is the form the web platform, `TextEncoder` and
 * every IndexedDB key comparison already assume, and because it is the smaller
 * change to the stored bytes.
 *
 * This departure is recorded as a SPEC DISAGREEMENT (see M0-STATUS.md). It is
 * taken now, in M0, before any user data exists, because the alternative is to
 * freeze a defect whose correction is an ANCHOR_SPACE_VERSION bump.
 * ────────────────────────────────────────────────────────────────────────── */

/* Every rule below is written with \u escapes rather than literal Arabic. In a
 * frozen contract the reviewer must be able to read the codepoints off the page
 * without trusting their editor's bidi rendering or their font's coverage. */

/** U+0640 ARABIC TATWEEL. */
const TATWEEL = /ـ/gu;

/**
 * Tashkeel and Qur'anic annotation marks. SPEC §3.1's ranges, with U+0653
 * (maddah above), U+0654 (hamza above) and U+0655 (hamza below) added — see
 * WHY U+0653..U+0655 above.
 */
const TASHKEEL = /[ً-ٰٕۖ-ۭ]/gu;

/** U+0622 آ, U+0623 أ, U+0625 إ, U+0671 ٱ → U+0627 ا */
const ALEF_VARIANTS = /[آأإٱ]/gu;

/** U+0649 ى → U+064A ي */
const ALEF_MAQSURA = /ى/gu;

/** U+0629 ة → U+0647 ه */
const TA_MARBUTA = /ة/gu;

/** Any run of whitespace. */
const WHITESPACE = /\s+/gu;

/**
 * The normalisation. Pure, synchronous, allocation-only — no clock, no locale,
 * no configuration. `fold(fold(s)) === fold(s)` for every input (asserted in
 * `anchor.spec.ts` over both `fc.string()` and all 6,236 ayat).
 *
 * `String.prototype.normalize` is a Unicode-version-dependent operation, and
 * that is worth naming out loud in a frozen contract: NFC for the Arabic block
 * has been stable since Unicode 1.1 and its composition exclusions are frozen
 * by the standard's stability policy, so the mapping this relies on cannot
 * change. It is not locale-sensitive and takes no options.
 */
export function fold(input: string): string {
	return input
		.normalize('NFC')
		.replace(TATWEEL, '')
		.replace(TASHKEEL, '')
		.replace(ALEF_VARIANTS, 'ا')
		.replace(ALEF_MAQSURA, 'ي')
		.replace(TA_MARBUTA, 'ه')
		.replace(WHITESPACE, ' ')
		.trim();
}

/* ───────────────────── canonical segmentation ─────────────────────────────
 *
 * A canonical word is a whitespace-delimited token of the vendored Tanzil
 * Uthmani text that contains at least one Arabic LETTER.
 *
 * The second half of that sentence is the whole of the rule, and it is not
 * decoration. The vendored file is the `marks=true&sajdah=true` variant, in
 * which the waqf signs (ۖ ۗ ۘ ۙ ۚ ۛ ۜ) and the sajdah sign (۩) appear as
 * STANDALONE space-delimited tokens — 4,379 of them. A naive `split(/\s+/)`
 * therefore counts 2:282 as 144 words instead of 128, and every word index in
 * every ayah containing a waqf mark is wrong.
 *
 * Because those tokens are dropped, the segmentation is IDENTICAL on the
 * `marks=false` variant of the same Tanzil release: 2:282 = 128 either way, all
 * 6,236 per-ayah counts equal. The anchor space does not depend on which
 * variant is vendored, which is what makes it safe for M1 to change that
 * choice later.
 *
 * Tatweel (U+0640) is excluded from the letter class on purpose: a token made
 * only of tatweel and marks is a decoration, not a word.
 *
 * Word indices are 0-based, and a word range is [start, end) — end exclusive —
 * matching `hafs:2:255:8-13` meaning words 8..12.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Arabic letters, as opposed to marks. Covers Arabic (U+0620..U+06FF, minus
 * tatweel, minus the combining/annotation blocks) and Arabic Supplement
 * (U+0750..U+077F). Deliberately NON-global: this regex is used with `.test()`,
 * and a `/g/` regex carries `lastIndex` state between calls.
 */
const ARABIC_LETTER = /[ؠ-ؿف-يٮٯٱ-ۓەۮۯۺ-ۿݐ-ݿ]/u;

/** Non-global twin of WHITESPACE, for `String.prototype.split`. */
const WHITESPACE_SPLIT = /\s+/u;

/** `true` iff a whitespace-delimited token counts as a canonical word. */
export function isWordToken(token: string): boolean {
	return ARABIC_LETTER.test(token);
}

/**
 * Split text into canonical words. Pure; the returned array is fresh.
 * `segment('')` and `segment('   ')` are both `[]`.
 *
 * This is the RAW tokeniser. For the text of a numbered ayah use
 * `canonicalWords(surah, ayah, text)`, which additionally applies the
 * basmala rule below — the two differ for the first ayah of 112 surahs, and
 * getting that wrong shifts every word index in those ayat by four.
 */
export function segment(text: string): string[] {
	const trimmed = text.trim();
	if (trimmed === '') return [];
	return trimmed.split(WHITESPACE_SPLIT).filter(isWordToken);
}

/** Canonical word count of a piece of free text (not of a numbered ayah). */
export function wordCount(text: string): number {
	return segment(text).length;
}

/* ───────────────────────── the basmala rule ───────────────────────────────
 *
 * THE VENDORED TANZIL TEXT PREPENDS THE BASMALA INTO THE TEXT OF AYAH 1.
 *
 * `sources/tanzil/quran-uthmani.txt` line `2|1|` reads
 * `بِسْمِ ٱللَّهِ ٱلرَّحْمَـٰنِ ٱلرَّحِيمِ الٓمٓ` — five whitespace tokens, of
 * which the first four are the basmala. That is true of the first ayah of every
 * surah EXCEPT al-Fatiha (where the basmala genuinely IS ayah 1, correctly four
 * words) and at-Tawbah (which has none). 112 ayat, 448 tokens.
 *
 * Counting those tokens as canonical words 0..3 of those ayat would be wrong in
 * four separate, independently fatal ways:
 *
 *  1. `hafs:2:1:0-1` would name بِسْمِ rather than الٓمٓ. The coordinate would
 *     not mean what its own grammar says it means.
 *  2. A whole-ayah note on 2:1 would store a `quote`, a `text_hash_raw` and a
 *     `text_hash_fold` computed over text that includes the basmala.
 *  3. Every mushaf renderer — QCF/QPC included — draws the basmala as a
 *     separate centred header, not as the opening words of ayah 1. The
 *     renderer's wordmap could therefore never agree with this segmentation for
 *     those 112 ayat, and under SPEC §3.2's renderer gate ("a renderer that
 *     cannot supply a wordmap to the canonical segmentation may not render word
 *     anchors") word anchors would have to be disabled on the opening ayah of
 *     112 surahs.
 *  4. SPEC §1.5 gate 5 requires the Tanzil whitespace-split length to equal the
 *     layout source's word count for all 6,236 ayat. It would fail for 112.
 *
 * SPEC §3.1 independently confirms which reading was intended: it writes the
 * dense global ordinal as `w ∈ 1..77430`. The vendored text segments to 77,881
 * canonical words; with the prepended basmala removed it is 77,433. The spec's
 * own arithmetic assumes the basmala-free segmentation.
 *
 * So the basmala is stripped HERE, in the derivation, and NOT from the vendored
 * file — which stays byte-identical with its copyright block intact, as the
 * Tanzil licence requires and as `--check` asserts.
 *
 * The strip is EVIDENCE-BASED, not positional: the leading four tokens are
 * dropped only if they fold to exactly the four basmala words. Two surahs (95
 * and 97) spell it بِّسْمِ with a shadda, which a byte comparison would miss and
 * `fold()` sees through. If a future re-pin of the source changes the wording,
 * the tokens are LEFT IN PLACE and `scripts/build-anchor-fixture.ts` fails
 * loudly rather than silently shifting 112 ayat by four.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The basmala, as four folded canonical words:
 * بسم الله الرحمن الرحيم.
 *
 * Written folded and \u-escaped so the comparison below is independent of which
 * harakat the source spells it with.
 */
export const BASMALA_FOLDED_WORDS: readonly string[] = [
	'بسم', //                                     بسم
	'الله', //                               الله
	'الرحمن', //                   الرحمن
	'الرحيم' //                     الرحيم
];

/** The surahs whose ayah 1 does NOT carry a prepended basmala in the source:
 *  al-Fatiha (where it is genuinely ayah 1) and at-Tawbah (which has none). */
const NO_PREPENDED_BASMALA = new Set([1, 9]);

/**
 * `true` iff `tokens` (the raw `segment()` output of the text of `surah:ayah`)
 * begins with a prepended basmala that the canonical segmentation must drop.
 *
 * Requires at least one word to survive the strip: an ayah that IS only the
 * basmala is al-Fatiha 1:1, which this rule never touches.
 */
export function hasPrependedBasmala(
	surah: number,
	ayah: number,
	tokens: readonly string[]
): boolean {
	if (ayah !== 1) return false;
	if (NO_PREPENDED_BASMALA.has(surah)) return false;
	if (tokens.length <= BASMALA_FOLDED_WORDS.length) return false;
	return BASMALA_FOLDED_WORDS.every((word, i) => fold(tokens[i] as string) === word);
}

/**
 * THE canonical words of a numbered ayah: `segment()` plus the basmala rule.
 * Every word index in every `ref` addresses this array.
 *
 * `text` is the body of the source line, i.e. everything after the second `|`.
 */
export function canonicalWords(surah: number, ayah: number, text: string): string[] {
	const tokens = segment(text);
	if (hasPrependedBasmala(surah, ayah, tokens)) {
		return tokens.slice(BASMALA_FOLDED_WORDS.length);
	}
	return tokens;
}

/** Canonical word count of a numbered ayah. Pairs with `wordCountOf(ayahId)`,
 *  which reads the same number out of the frozen fixture. */
export function canonicalWordCount(surah: number, ayah: number, text: string): number {
	return canonicalWords(surah, ayah, text).length;
}

/**
 * The canonical rendering of a run of words back into one string, and the exact
 * input to both `text_hash_raw` and `fold()` before `text_hash_fold`. A single
 * U+0020 between words, nothing else — so the hash does not depend on how the
 * source file happened to space or mark the span.
 */
export function spanText(words: readonly string[]): string {
	return words.join(' ');
}

/**
 * The canonical span for a word anchor over an already-segmented word array:
 * words `[start, end)`.
 *
 * Out-of-range indices clamp rather than throw — a span that runs off the end
 * of a shortened ayah is exactly the tier-3 case `drift.ts` must be able to
 * evaluate, and it needs the truncated span to compare, not an exception.
 */
export function spanOfWordList(words: readonly string[], start: number, end: number): string {
	const lo = Math.max(0, Math.min(start, words.length));
	const hi = Math.max(lo, Math.min(end, words.length));
	return spanText(words.slice(lo, hi));
}

/**
 * The canonical span for a word anchor over free text. Uses the RAW tokeniser,
 * so it is correct for anything that is not the first ayah of a surah; for a
 * numbered ayah use `canonicalSpanOfWords`.
 */
export function spanOfWords(text: string, start: number, end: number): string {
	return spanOfWordList(segment(text), start, end);
}

/** The canonical span for a word anchor into a NUMBERED ayah — basmala rule
 *  applied. This is what the read path and `drift.ts`'s caller must use. */
export function canonicalSpanOfWords(
	surah: number,
	ayah: number,
	text: string,
	start: number,
	end: number
): string {
	return spanOfWordList(canonicalWords(surah, ayah, text), start, end);
}

/** The canonical span for a whole ayah: every word, canonically joined. */
export function spanOfAyah(text: string): string {
	return spanText(segment(text));
}

/** The canonical span for a whole NUMBERED ayah — basmala rule applied. */
export function canonicalAyahText(surah: number, ayah: number, text: string): string {
	return spanText(canonicalWords(surah, ayah, text));
}

/**
 * Build the stored `quote` (SPEC §3.1, Layer 3): the verbatim span, capped at
 * `max` code points. If the cap bites, the trailing partial word is dropped, so
 * `fold(quote)` stays a clean whitespace-aligned prefix of `fold(span)` and the
 * tier-2 word-boundary test in `drift.ts` remains meaningful.
 *
 * The default is `QUOTE_MAX_CHARS` from `types.ts` — imported rather than
 * re-typed, because two independent copies of one frozen number is exactly the
 * arrangement this module set exists to forbid.
 *
 * Pure: no clock, no truncation marker, no ellipsis character. The quote is
 * evidence, and evidence does not get punctuation added to it.
 */
export function makeQuote(span: string, max: number = QUOTE_MAX_CHARS): string {
	const points = Array.from(span);
	if (points.length <= max) return span;
	const cut = points.slice(0, max).join('');
	const lastSpace = cut.lastIndexOf(' ');
	return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd();
}
